import {
    APPLICANT_ATTRIBUTION_BOTH,
    CHILD_BENEFIT_TYPES,
    INFORMATION_SOURCE,
    LOAN_REQUEST_USAGES,
    REAL_ESTATE_TYPES,
    REAL_ESTATE_USAGES,
} from 'cpool-constants';
import { schemaLoanRequest } from 'cpool-schemas';
import type { z } from 'zod';

import { censorLoanRequest, censorLoanRequestApplicant2Only } from '../../utils/censor.utils';
import type { EventSchema } from '../../utils/EventSchema';
import { _Event } from '../_Event';

export class LoanRequest_Created extends _Event {
    constructor(payload: EventSchema<LoanRequest_Created>, deprecated = false) {
        super(payload, deprecated);
    }

    getSchema() {
        return schemaLoanRequest;
    }

    getCensoredSchema() {
        return this.getSchema().transform(censorLoanRequest);
    }

    static example(): EventSchema<LoanRequest_Created> {
        return {
            coApplicantHidden: false,
            correlationWebsite: null,
            customId: null,
            finanzbedarf: {
                fahrzeugkauf: null,
                finanzierungswunsch: {
                    kreditbetrag: 18000,
                    laufzeitInMonaten: 60,
                },
                finanzierungszweck: LOAN_REQUEST_USAGES.UMSCHULDUNG,
            },
            haushalt: {
                ausgaben: {
                    mietausgaben: [
                        {
                            betragMonatlich: 500,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            id: 'f992515d-407c-413c-887a-76a90157edfd',
                        },
                    ],
                    privateKrankenversicherungen: [
                        {
                            betragMonatlich: 500,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            id: 'f992515d-407c-413c-887a-76a90157edfc',
                        },
                    ],
                    sonstigeAusgaben: [
                        {
                            betragMonatlich: 500,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            id: 'f992515d-407c-413c-887a-76a90157edfb',
                        },
                    ],
                    unterhaltsverpflichtungen: [
                        {
                            betragMonatlich: 500,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            id: 'f992515d-407c-413c-887a-76a90157edfa',
                        },
                    ],
                },
                einnahmen: {
                    ehegattenunterhalt: [
                        {
                            betragMonatlich: 500,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            id: 'f992515d-407c-413c-887a-76a90157edf9',
                        },
                    ],
                    einkuenfteAusKapitalvermoegen: [
                        {
                            betragMonatlich: 500,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            id: 'f992515d-407c-413c-887a-76a90157edf8',
                        },
                    ],
                    einkuenfteAusNebentaetigkeit: [
                        {
                            beginnDerTaetigkeit: new Date('2023-07-17'),
                            betragMonatlich: 500,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            id: 'f992515d-407c-413c-887a-76a90157edf7',
                        },
                    ],
                    sonstigeEinnahmen: [
                        {
                            betragMonatlich: 500,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            id: 'f992515d-407c-413c-887a-76a90157edf6',
                            notiz: 'Einnahmen aus Vermietung',
                        },
                    ],
                    unbefristeteZusatzrenten: [
                        {
                            betragMonatlich: 500,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            id: 'f992515d-407c-413c-887a-76a90157edf5',
                        },
                    ],
                },
                immobilien: [
                    {
                        baubeschreibung: null,
                        baufinanzierungsrate: 1000,
                        bezeichnung: 'bezeichnung',
                        darlehen: [
                            {
                                rateMonatlich: 5000,
                                restschuld: 5000,
                                zinsbindungBis: new Date('2020-09-01'),
                            },
                        ],
                        gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                        id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
                        immobilienart: REAL_ESTATE_TYPES.EIGENTUMSWOHNUNG,
                        mieteinnahmenKaltMonatlich: 1500,
                        mieteinnahmenWarmMonatlich: 2000,
                        nebenkostenMonatlich: 2500,
                        nutzungsart: REAL_ESTATE_USAGES.VERMIETET,
                        vermieteteWohnflaeche: 3000,
                        wert: 3500,
                        wohnflaeche: 4000,
                    },
                ],
                kinder: [
                    {
                        alter: 7,
                        gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                        id: 'f992515d-407c-413c-887a-76a90157edf4',
                        kindergeldFuer: CHILD_BENEFIT_TYPES.ERSTES_ODER_ZWEITES_KIND,
                        kindergeldHoehe: 200,
                        unterhaltseinnahmenMonatlich: 500,
                    },
                ],
                kontoverbindung: {
                    bic: null,
                    gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                    iban: 'DE17643500700021542743',
                    kreditinstitut: 'Kr Spk Tuttlingen',
                },
                verbindlichkeiten: {
                    dispositionskredite: [
                        {
                            abloesen: true,
                            beanspruchterBetrag: null,
                            bic: null,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            glaeubiger: null,
                            iban: 'DE17643500700021542743',
                            id: 'e6d6f4c2-3f7d-4b2a-9c8c-1e6b6b6f2f4e',
                            kreditinstitut: null,
                            source: INFORMATION_SOURCE.FORMULAR,
                            verfuegungsrahmen: null,
                            zinssatz: null,
                        },
                    ],
                    geschaeftskredite: [
                        {
                            abloesen: true,
                            bic: null,
                            datumErsteZahlung: new Date('2019-08-01'),
                            datumLetzteRate: new Date('2024-07-01'),
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            glaeubiger: null,
                            iban: null,
                            id: '67a9eed4-3471-42fd-9749-da18c0a612b7',
                            kreditinstitut: null,
                            kreditLaufzeit: null,
                            rateMonatlich: 506,
                            restschuld: 5919,
                            schlussrate: null,
                            source: INFORMATION_SOURCE.FORMULAR,
                            urspruenglicherKreditbetrag: 22000,
                            zinssatz: null,
                        },
                    ],
                    immobiliendarlehen: [
                        {
                            abloesen: true,
                            bic: null,
                            datumErsteZahlung: new Date('2019-08-01'),
                            datumLetzteRate: new Date('2024-07-01'),
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            glaeubiger: null,
                            iban: null,
                            id: '67a9eed4-3471-42fd-9749-da18c0a612b7',
                            kreditinstitut: null,
                            kreditLaufzeit: null,
                            rateMonatlich: 506,
                            restschuld: 5919,
                            schlussrate: null,
                            source: INFORMATION_SOURCE.FORMULAR,
                            urspruenglicherKreditbetrag: 22000,
                            zinssatz: null,
                        },
                    ],
                    kontokorrentkredite: [
                        {
                            beanspruchterBetrag: null,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            glaeubiger: null,
                            id: '569c8071-e7fa-4851-8417-49eebddf5681',
                            source: INFORMATION_SOURCE.FORMULAR,
                            verfuegungsrahmen: null,
                            zinssatz: null,
                        },
                    ],
                    kreditkarten: [
                        {
                            abloesen: true,
                            beanspruchterBetrag: null,
                            bic: null,
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            glaeubiger: null,
                            iban: 'DE17643500700021542743',
                            id: '7cf54d90-5753-44fc-be16-feac605af065',
                            kreditinstitut: null,
                            rateMonatlich: null,
                            source: INFORMATION_SOURCE.FORMULAR,
                            verfuegungsrahmen: null,
                            zinssatz: null,
                        },
                    ],
                    leasings: [
                        {
                            datumLetzteRate: new Date('2024-07-01'),
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            glaeubiger: null,
                            id: '22097497-e7b6-402a-a65b-6c02c049d067',
                            rateMonatlich: 360,
                            schlussrate: null,
                            source: INFORMATION_SOURCE.FORMULAR,
                            zinssatz: null,
                        },
                    ],
                    ratenkredite: [
                        {
                            abloesen: true,
                            bic: null,
                            datumErsteZahlung: new Date('2019-08-01'),
                            datumLetzteRate: new Date('2024-07-01'),
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            glaeubiger: null,
                            iban: null,
                            id: '67a9eed4-3471-42fd-9749-da18c0a612b7',
                            kreditinstitut: null,
                            kreditLaufzeit: null,
                            rateMonatlich: 506,
                            restschuld: 5919,
                            schlussrate: null,
                            source: INFORMATION_SOURCE.FORMULAR,
                            urspruenglicherKreditbetrag: 22000,
                            zinssatz: null,
                        },
                        {
                            abloesen: true,
                            bic: null,
                            datumErsteZahlung: new Date('2019-08-01'),
                            datumLetzteRate: new Date('2024-07-01'),
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            glaeubiger: null,
                            iban: null,
                            id: '22097497-e7b6-402a-a65b-6c02c049d067',
                            kreditinstitut: null,
                            kreditLaufzeit: null,
                            rateMonatlich: 360,
                            restschuld: 10900,
                            schlussrate: null,
                            source: INFORMATION_SOURCE.FORMULAR,
                            urspruenglicherKreditbetrag: 11000,
                            zinssatz: null,
                        },
                    ],
                    sonstigeVerbindlichkeiten: [
                        {
                            abloesen: true,
                            bic: null,
                            datumErsteZahlung: new Date('2019-08-01'),
                            datumLetzteRate: new Date('2024-07-01'),
                            gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
                            glaeubiger: null,
                            iban: 'DE17643500700021542743',
                            id: 'f9206718-401a-407b-ac7b-06ecdbb2b9fb',
                            kreditinstitut: 'Kr Spk Tuttlingen',
                            kreditLaufzeit: null,
                            rateMonatlich: 506,
                            restschuld: 5919,
                            schlussrate: null,
                            source: INFORMATION_SOURCE.FORMULAR,
                            urspruenglicherKreditbetrag: 22000,
                            zinssatz: null,
                        },
                    ],
                },
            },
            isFeasible: false,
            isTestLead: false,
            leadTS: null,
            organization: 'kredit.de',
            originalApplicants: ['c5afcfcb-8843-4995-8e14-dfca3f1323d4'],
            tracking: {
                adcref: 'adcref',
                agent: 'agent',
                bid: 'bid',
                consentString: 'consentString',
                directPartnerSourceId: 'directPartnerSourceId',
                directPartnerSubSourceId: 'directPartnerSubSourceId',
                entryUri: 'entryUri',
                fbc: 'fbc',
                fbclid: 'fbclid',
                fbp: 'fbp',
                fpid: 'fpid',
                ga_cid: 'ga_cid',
                gclid: 'gclid',
                optout_adcell: 'optout_adcell',
                optout_facebook: 'optout_facebook',
                optout_GA: 'optout_GA',
                optout_pinterest: 'optout_pinterest',
                optout_taboola: 'optout_taboola',
                pid: 'pid',
                referer: 'referer',
                sessionId: 'sessionId',
                tbclid: 'tbclid',
                team: 'team',
                uri: 'uri',
                userAgent: 'userAgent',
            },
            utm: {
                campaign: 'campaign',
                content: 'content',
                medium: 'medium',
                source: 'source',
                term: 'term',
            },
        };
    }

    getCensoredSchemaApplicant2Only(_applicant2Id: string) {
        return this.getSchema().transform(censorLoanRequestApplicant2Only);
    }

    static exampleCensoredApplicant2Only() {
        return censorLoanRequestApplicant2Only(LoanRequest_Created.example());
    }

    static exampleCensored(): z.infer<ReturnType<LoanRequest_Created['getCensoredSchema']>> {
        return censorLoanRequest(LoanRequest_Created.example());
    }
}
